/**
 * audio — the single facade the game talks to. Everything else in this folder
 * is an implementation detail.
 *
 *   audio.init(camera, origin)   once, after the camera + floating origin exist
 *   audio.unlock()               on a user gesture (launch click) — starts sound
 *   audio.update(frame)          once per render frame, near the end of the loop
 *   audio.sfx(id, { at })        fire a one-shot (positional if `at` given)
 *   audio.impact / footstep / …  semantic helpers used at game choke-points
 *   audio.notifyCombat(kind)     combat music: 'hurt' starts it, two 'hit's start it, 'act' only sustains
 *   audio.set{Master,Music,Fx}Volume / setMuted   settings UI + persistence
 *
 * Design notes wired to THIS codebase:
 *  - The listener rides the one shared `camera`, so every mode (foot/flight) is
 *    covered by a single listener. Positions are passed in SYSTEM space and
 *    rebased here by the live floating `origin` (captured by reference), so the
 *    origin snap is transparent to callers.
 *  - There is no event bus in the game; this facade is called directly from the
 *    choke-points the trace identified. That matches the codebase's style.
 *  - Nothing plays until `unlock()` runs on a gesture; before that, update() and
 *    sfx() are cheap no-ops.
 */
import * as THREE from "three";
import { AudioEngine, type BusName } from "./engine";
import { SoundBank } from "./bank";
import { SfxPlayer, type Vec3 } from "./voices";
import { BedManager } from "./beds";
import { MusicDirector } from "./music";
import { SynthMusicDirector,type MusicTransport } from './synth/director';
import { silentMusic } from './synth/reactivity';
import type { SynthRequest } from './synth/compose';
import type { PlaceContext } from './place/context';
import { SOUNDS } from "./manifest";
import { dialogueVoiceCue } from "./dialogue";
import type { DialogueGlyph } from "../../../shared/src/social/dialogue";
import type { BodyId } from "../../../shared/src/rpg";
import { FootstepPlayback, FOOTSTEP_IDS, LANDING_IDS, footstepId, landingId, type FootstepSurface } from './footsteps';

/** Everything update() needs, read structurally so we don't import game types. */
export interface AudioFrame {
  dt: number;
  /** launched && in a real world (not the void/outfit). Gates ambience+music. */
  active: boolean;
  mode: string; // "intro"|"fly"|"foot"|"jump"|"docked"|"builder"|"outfit"|"menu"
  onFoot: boolean;
  // --- on-foot movement ---
  speed: number; // groundSpeed (m/s)
  grounded: boolean;
  waterState: string; // "land"|"wade"|"swim"
  submerge: number; // 0..1 head-underwater
  jet: string; // "none"|"hover"|"glide"
  boostK: number; // 0..1
  flightPower?:number;
  crouching: boolean;
  impactSpeed: number; // last touchdown radial speed (m/s)
  jumpChain: number; // 0 grounded, 1 jumped, 2 double, 3 gliding
  iframeT: number; // dodge i-frames remaining (s)
  /** Queried only on a footfall or landing. Uses live flooring, independent of biome. */
  footstepSurface?: (side: 0 | 1) => FootstepSurface;
  /** Contacts consumed from the player's finished animation pose this frame. */
  footfalls?: number;
  // --- environment at the player ---
  atmosphere: boolean;
  hasSea: boolean;
  biome: string; // planet archetype
  place?: PlaceContext;
  precip: number; // 0..1
  precipKind: number; // 0 none, 1 rain, 2 snow
  windSpeed: number; // 0..1
  /** Generated vents, in system space; the normal bed lifecycle owns fading. */
  volcanoes?: {id:string;pos:Vec3;level:number}[];
  // --- ship ---
  shipMode: string; // "flight"|"boost"|"pulse"|"landed"
  shipThrottle: number;
  shipSpeed: number;
  shipPos: Vec3; // local ship position (system space) — positional booster
  // --- local ground vehicle ---
  vehicle?: {
    speed: number;
    maxSpeed: number;
    boost: number;
  };
  // --- other entities that emit world sound (positional; distance matters) ---
  /** Remote ships: system-space position + signed m/s speed (drives boosters). */
  ships: { id: number; pos: Vec3; speed: number }[];
  /** Remote on-foot players: position + jet state ("none"|"hover"|"glide"). */
  jetters: { id: number; pos: Vec3; jet: string; power?:number }[];
}

const SHIP_BOOST_MIN_SPEED = 250; // remote ship below this → no booster sound
const SHIP_PULSE_MIN_SPEED = 6000; // remote ship above this → the bassier pulse rumble
const SHIP_AUDIO_MAXDIST = 7000; // cull remote ship audio beyond this (m)
const JET_AUDIO_MAXDIST = 260; // cull remote jetpack audio beyond this (m)
const FIGHT_HOLD = 14; // s of calm after the last combat event before the battle music ends
const CRITICAL: readonly string[] = [
  "dialogue.low", "dialogue.warm", "dialogue.bright",
  "dialogue.low.bold", "dialogue.warm.bold", "dialogue.bright.bold",
  "weapon.player.fire", "weapon.remote.fire", "weapon.drone.fire",
  "impact.terrain", "impact.body", "explosion.drone",
  ...FOOTSTEP_IDS, ...LANDING_IDS,
  "jump", "dodge",
  "vehicle.impact",
  "bed.vehicle.engine",
  "melee.swing", "melee.hit", "combat.block", "combat.parry", "player.hurt",
  "ui.click", "ui.select",
];

class Audio {
  private engine: AudioEngine | null = null;
  private bank!: SoundBank;
  private sfxPlayer!: SfxPlayer;
  private beds!: BedManager;
  private music!: MusicDirector;
  private synthMusic!: SynthMusicDirector;

  private camera: THREE.Object3D | null = null;
  private origin = new THREE.Vector3(); // captured by reference at init
  private unlocked = false;
  private dialogueEpoch = 0;

  // persisted prefs (mirrored to the engine)
  private prefMaster = 0.9;
  private prefMusic = 0.55;
  private prefFx = 0.85;
  private prefMuted = false;

  // per-frame transient state
  private footsteps = new FootstepPlayback();
  private prevGrounded = true;
  private prevFootActive = false;
  private prevJumpChain = 0;
  private prevIframe = 0;
  private prevWater = "land";
  private miningOn = false;
  private miningPos: THREE.Vector3 | null = null;

  private _rp: Vec3 = { x: 0, y: 0, z: 0 }; // scratch render-space position

  /** Create the graph (context starts suspended) and capture live refs. */
  init(camera: THREE.Object3D, origin: THREE.Vector3): void {
    if (this.engine) return;
    try {
      this.engine = new AudioEngine(camera);
    } catch (e) {
      console.warn("[audio] Web Audio unavailable — running silent", e);
      return;
    }
    this.camera = camera;
    this.origin = origin;
    this.bank = new SoundBank(this.engine.ctx);
    this.sfxPlayer = new SfxPlayer(this.engine, this.bank);
    this.beds = new BedManager(this.engine, this.bank);
    this.music = new MusicDirector(this.engine);
    this.synthMusic = new SynthMusicDirector(this.engine);

    this.loadPrefs();

    // First interaction anywhere unlocks (covers the auth screen too).
    const onGesture = (): void => void this.unlock();
    window.addEventListener("pointerdown", onGesture);
    window.addEventListener("keydown", onGesture);
    // One delegated click → UI tick for every <button> in the app.
    window.addEventListener(
      "click",
      (e) => {
        if (!this.unlocked) return;
        const el = e.target as HTMLElement | null;
        if (el?.closest("button")) this.sfx("ui.click");
      },
      true,
    );

    if (import.meta.env.DEV) {
      (window as unknown as { __audio: unknown }).__audio = {
        play: (id: string) => this.sfx(id),
        playAt: (id: string, x: number, y: number, z: number) => this.sfx(id, { at: new THREE.Vector3(x, y, z) }),
        list: () => Object.keys(SOUNDS),
        musicPool: (p: string | null) => this.music.setPool(p), // "planet"|"flight"|"menu"|null
        musicPlayNow: () => this.music.playNow(), // skip the gap — start a track now
        musicCurrent: () => this.music.playing, // playing track id, or null during a gap
        synthMusic: () => this.synthMusic.status,
        combat: (kind: "hurt" | "hit" | "act") => this.notifyCombat(kind),
        fighting: () => this.fighting,
        combatCalm: () => { this.fightUntil = 0; this.combatHeat = 0; }, // skip the 14 s hold (probes)
        test: () => this.audition(),
        engine: () => this.engine,
        state: () => this.engine?.ctx.state ?? "none",
        unlocked: () => this.unlocked,
        update: (f: AudioFrame) => this.update(f),
        /** Resolve an id to its baked/decoded buffer duration (file-or-synth path). */
        bake: (id: string) => this.bank.load(id).then((b) => (b ? b.duration : null)),
      };
    }
  }

  /** Resume the context on a gesture and warm the critical SFX. Idempotent. */
  async unlock(): Promise<void> {
    if (!this.engine || this.unlocked) return;
    await this.engine.resume();
    if (!this.engine.unlocked) return; // gesture not honoured; try again next one
    this.unlocked = true;
    void this.bank.preload(CRITICAL);
  }

  // -------------------------------------------------------------------------
  // One-shot SFX
  // -------------------------------------------------------------------------

  /** Fire a one-shot. `at` is SYSTEM space (rebased here) for spatial sounds. */
  sfx(id: string, opts: { at?: THREE.Vector3; gain?: number; rate?: number } = {}): void {
    if (!this.unlocked || !this.engine) return;
    const def = SOUNDS[id];
    if (def?.spatial && opts.at && this.camera) {
      this._rp.x = opts.at.x - this.origin.x;
      this._rp.y = opts.at.y - this.origin.y;
      this._rp.z = opts.at.z - this.origin.z;
      const c = this.camera.position;
      const dist = Math.hypot(this._rp.x - c.x, this._rp.y - c.y, this._rp.z - c.z);
      this.sfxPlayer.play(id, { pos: this._rp, dist, gain: opts.gain, rate: opts.rate });
    } else {
      this.sfxPlayer.play(id, { gain: opts.gain, rate: opts.rate });
    }
  }

  /** Bolt/melee impact → terrain vs body sound at a system-space point. */
  impact(kind: string, at: THREE.Vector3): void {
    this.sfx(kind === "terrain" ? "impact.terrain" : "impact.body", { at });
  }

  /** Combat → battle music. Graded so target practice never starts a fight:
   *  'hurt' (we took damage) starts it at once; 'hit' (our attack landed on a body) starts
   *  it on the second hit within a few seconds; 'act' (firing, swinging, misses, other
   *  players' hits) only keeps an existing fight going. It ends FIGHT_HOLD s after the last
   *  combat event. */
  notifyCombat(kind: "hurt" | "hit" | "act" = "act"): void {
    const now = performance.now() / 1000;
    this.combatHeat = this.combatHeat * Math.pow(.5, (now - this.combatHeatAt) / 3) + (kind === "hit" ? 1 : 0);
    this.combatHeatAt = now;
    if (kind === "hurt" || this.combatHeat >= 1.9 || now < this.fightUntil) this.fightUntil = now + FIGHT_HOLD;
  }
  private combatHeat = 0;
  private combatHeatAt = 0;
  private fightUntil = 0;
  /** Is battle music wanted right now? (Exposed for probes.) */
  get fighting(): boolean { return performance.now() / 1000 < this.fightUntil; }

  dialogue(id: string, traits: readonly string[], char: string | null, style?: DialogueGlyph["style"], body: BodyId = "male"): void {
    if (!char) { this.stopDialogue(); return; }
    const cue = dialogueVoiceCue(id, traits, char, style, body);
    if (cue) this.sfx(cue.id, { rate: cue.rate, gain: cue.gain });
  }
  stopDialogue(): void { this.dialogueEpoch++; this.sfxPlayer?.stopPrefix("dialogue."); }
  async previewDialogue(id: string, traits: readonly string[], body: BodyId = "male", style: DialogueGlyph["style"] = {}): Promise<void> {
    const epoch = this.dialogueEpoch, cue = dialogueVoiceCue(id, traits, "a", style, body)!;
    await this.unlock();
    if (!this.unlocked) return;
    await this.bank.load(cue.id);
    if (epoch === this.dialogueEpoch) this.sfx(cue.id, { rate: cue.rate, gain: cue.gain });
  }

  /** Drive the (positional) mining-beam ambience; pos is SYSTEM space. */
  setMining(on: boolean, at?: THREE.Vector3): void {
    this.miningOn = on;
    this.miningPos = on && at ? (this.miningPos ?? new THREE.Vector3()).copy(at) : null;
  }

  // -------------------------------------------------------------------------
  // Per-frame brain
  // -------------------------------------------------------------------------
  update(f: AudioFrame): void {
    if (!this.engine || !this.unlocked) return;

    // Underwater muffle tracks head submersion in every mode.
    this.engine.setMuffle(f.submerge);

    const landed = this.updateDiscrete(f);
    if (!landed) this.updateFootsteps(f);
    this.updateBeds(f);
    this.updateMusic(f);
  }

  /** Edge-triggered movement events (jump/land/dodge/water) from prev state. */
  private updateDiscrete(f: AudioFrame): boolean {
    let landed = false;
    const footActive = f.active && f.onFoot && f.mode === 'foot';
    if (f.active && f.onFoot) {
      if (f.jumpChain > this.prevJumpChain && f.jumpChain >= 1) this.sfx("jump");
      if (footActive && this.prevFootActive && f.grounded && !this.prevGrounded
        && f.jet === 'none' && f.waterState !== 'swim' && this.prevWater !== 'swim') {
        for (const contact of this.footsteps.landing(f.impactSpeed)) {
          this.sfx(landingId(this.contactSurface(f, contact.side), contact.variant), { gain: contact.gain, rate: contact.rate });
          landed = true;
        }
      }
      if (f.iframeT > 0 && this.prevIframe <= 0) this.sfx("dodge");
      if (f.waterState !== this.prevWater) {
        if (this.prevWater === "land" && f.waterState !== "land" && !landed) this.sfx("water.enter");
        else if (f.waterState === "land" && this.prevWater !== "land") this.sfx("water.splash");
      }
    }
    this.prevGrounded = f.grounded;
    this.prevFootActive = footActive;
    this.prevJumpChain = f.jumpChain;
    this.prevIframe = f.iframeT;
    this.prevWater = f.waterState;
    return landed;
  }

  private contactSurface(f: AudioFrame, side: 0 | 1): FootstepSurface {
    return f.waterState === 'wade' ? 'water' : f.footstepSurface?.(side) ?? 'stone';
  }

  /** Animation-triggered footsteps (player's own steps → 2D). */
  private updateFootsteps(f: AudioFrame): void {
    for (const step of this.footsteps.update(f)) {
      const surface = this.contactSurface(f, step.side);
      this.sfx(footstepId(surface, step.variant), { gain: step.gain });
    }
  }

  /** Continuous beds, leveled from state. Singletons fade; keyed positional
   *  emitters (ship boosters, remote jets) are reaped by endFrame() when gone. */
  private updateBeds(f: AudioFrame): void {
    const bed = (id: string, level: number, opts?: { rate?: number; pos?: Vec3; key?: string }): void => {
      if (level > 0.001) this.beds.set(id, level, opts);
    };

    if (f.active) {
      // Weather beds play whenever the viewer is standing in an atmosphere — on
      // foot OR in a ground vehicle (both set `atmosphere`) — not just on foot.
      if (f.atmosphere) {
        bed("bed.wind", 0.12 + f.windSpeed * 0.7);
        if (f.precipKind === 1) bed("bed.rain", Math.min(1, f.precip * 1.1));
        if (f.hasSea) bed("bed.ocean", f.waterState !== "land" ? 0.7 : 0.18);
        if (f.submerge > 0.4) bed("bed.underwater", Math.min(1, (f.submerge - 0.4) / 0.4));
      }
      if (f.onFoot && f.atmosphere) {
        const power=f.flightPower??1;
        const jetL = f.jet === "glide" ? .06+.79*power : f.jet === "hover" ? .16+.44*power : f.boostK * 0.7;
        bed("bed.jet", Math.min(1, jetL)); // YOUR jetpack — 2D (it's on your back)
      }

      // SHIP BOOSTERS — POSITIONAL at the ship so distance matters (your own under
      // chase-cam pulling away, and every remote ship). Local uses the exact mode;
      // remotes are inferred from SPEED (boost state isn't networked, but only
      // boosting/pulsing reaches these speeds). Pulse wins over boost.
      const cam = this.camera?.position;
      for(const v of f.volcanoes??[]) {
        const pos=this.renderOf(v.pos);
        if(cam&&Math.hypot(pos.x-cam.x,pos.y-cam.y,pos.z-cam.z)>6000)continue;
        bed('bed.volcano',v.level,{key:v.id,pos,rate:.8+.25*v.level});
      }
      if (!f.onFoot && (f.shipMode === "boost" || f.shipMode === "pulse")) {
        const pulse = f.shipMode === "pulse";
        const id = pulse ? "bed.ship.pulse" : "bed.ship.boost";
        const lvl = pulse ? 0.6 + Math.min(0.4, f.shipSpeed / 20000) : 0.55 + Math.min(0.45, f.shipSpeed / 6000);
        const rate = pulse ? 0.78 + Math.min(0.3, f.shipSpeed / 30000) : 0.8 + Math.min(0.35, f.shipSpeed / 9000);
        bed(id, lvl, { key: "self", pos: this.renderOf(f.shipPos), rate });
      }
      for (const s of f.ships ?? []) {
        const sp = Math.abs(s.speed);
        if (sp < SHIP_BOOST_MIN_SPEED) continue;
        const pos = this.renderOf(s.pos);
        if (cam && Math.hypot(pos.x - cam.x, pos.y - cam.y, pos.z - cam.z) > SHIP_AUDIO_MAXDIST) continue;
        const pulse = sp > SHIP_PULSE_MIN_SPEED;
        const id = pulse ? "bed.ship.pulse" : "bed.ship.boost";
        const lvl = pulse ? 0.5 + Math.min(0.4, (sp - SHIP_PULSE_MIN_SPEED) / 26000) : 0.4 + Math.min(0.45, (sp - SHIP_BOOST_MIN_SPEED) / 8000);
        const rate = pulse ? 0.78 + Math.min(0.3, sp / 30000) : 0.8 + Math.min(0.35, sp / 9000);
        bed(id, lvl, { key: `s${s.id}`, pos, rate });
      }

      // LOCAL GROUND VEHICLE — listener-centric like the local jetpack. The
      // continuous bed lets pitch follow speed/load without audible restarts.
      if (f.vehicle) {
        // speedK can exceed 1 while boosting (the overdrive lifts the cap), so the
        // engine screams a little at overdrive top speed. Off-boost, pitch is driven
        // almost entirely by speed (a low idle hum that climbs as you accelerate);
        // the boost term is a strong, distinct upward REV layered on top.
        const speedK = Math.min(1.25, Math.abs(f.vehicle.speed) / Math.max(1, f.vehicle.maxSpeed));
        const load = Math.min(1, speedK * 0.7 + f.vehicle.boost * 0.6);
        bed("bed.vehicle.engine", 0.28 + load * 0.72, { rate: 0.55 + speedK * 0.55 + f.vehicle.boost * 0.5 });
      }

      // REMOTE players' jetpacks — POSITIONAL, one keyed emitter per jetting player.
      for (const j of f.jetters ?? []) {
        if (j.jet !== "hover" && j.jet !== "glide") continue;
        const pos = this.renderOf(j.pos);
        if (cam && Math.hypot(pos.x - cam.x, pos.y - cam.y, pos.z - cam.z) > JET_AUDIO_MAXDIST) continue;
        bed("bed.jet.remote", j.jet === "glide" ? .06+.79*(j.power??1) : .16+.44*(j.power??1), { key: `j${j.id}`, pos });
      }

      if (this.miningOn) {
        const opts = this.miningPos ? { pos: this.renderOf(this.miningPos) } : undefined;
        bed("bed.mining", 0.8, opts);
      }
    }
    this.beds.endFrame();
  }

  /** Seeded synth music: the surface place's scene, or Space drive while the ship flies.
   *  (The chamber place director and the fixed flight MP3 pool are retired from play.) */
  private updateMusic(f: AudioFrame): void {
    const surface = f.active && (f.mode === 'foot' || f.mode === 'vehicle');
    this.synthMusic.update(surface ? f.place ?? null : null, f.active && f.mode === 'fly', f.active && this.fighting);
    this.music.setPool(null);
    this.music.update(f.dt);
  }

  /** Explicit synth music for standalone modes (the race): null = silence. */
  setMusic(request: SynthRequest | null,transport:MusicTransport|null=null): void {
    this.synthMusic?.play(request,transport);
  }

  musicReactivity(dt: number) { return this.synthMusic?.sampleMusic(dt) ?? silentMusic(); }

  private renderOf(p: Vec3): Vec3 {
    this._rp.x = p.x - this.origin.x;
    this._rp.y = p.y - this.origin.y;
    this._rp.z = p.z - this.origin.z;
    return this._rp;
  }

  // -------------------------------------------------------------------------
  // Settings + persistence
  // -------------------------------------------------------------------------
  private loadPrefs(): void {
    const num = (k: string, d: number): number => {
      const v = parseFloat(localStorage.getItem(k) ?? "");
      return Number.isFinite(v) ? v : d;
    };
    this.prefMaster = num("hl-vol-master", this.prefMaster);
    this.prefMusic = num("hl-vol-music", this.prefMusic);
    this.prefFx = num("hl-vol-fx", this.prefFx);
    this.prefMuted = localStorage.getItem("hl-muted") === "1";
    this.applyPrefs();
  }

  private applyPrefs(): void {
    if (!this.engine) return;
    this.engine.setMasterVolume(this.prefMaster);
    this.engine.setBusVolume("music", this.prefMusic);
    for (const b of ["sfx", "ambient", "ui"] as BusName[]) this.engine.setBusVolume(b, this.prefFx);
    this.engine.setMuted(this.prefMuted);
  }

  prefs(): { master: number; music: number; fx: number; muted: boolean } {
    return { master: this.prefMaster, music: this.prefMusic, fx: this.prefFx, muted: this.prefMuted };
  }

  setMasterVolume(v: number): void {
    this.prefMaster = v;
    localStorage.setItem("hl-vol-master", String(v));
    this.engine?.setMasterVolume(v);
  }

  setMusicVolume(v: number): void {
    this.prefMusic = v;
    localStorage.setItem("hl-vol-music", String(v));
    this.engine?.setBusVolume("music", v);
  }

  setFxVolume(v: number): void {
    this.prefFx = v;
    localStorage.setItem("hl-vol-fx", String(v));
    if (this.engine) for (const b of ["sfx", "ambient", "ui"] as BusName[]) this.engine.setBusVolume(b, v);
  }

  setMuted(on: boolean): void {
    this.prefMuted = on;
    localStorage.setItem("hl-muted", on ? "1" : "0");
    this.engine?.setMuted(on);
  }

  /** DEV: audition every one-shot in sequence (window.__audio.test()). */
  private audition(): void {
    const ids = Object.keys(SOUNDS);
    ids.forEach((id, i) => window.setTimeout(() => this.sfx(id), i * 500));
  }
}

/** The singleton every system imports. */
export const audio = new Audio();
